import React, { useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from '../../Themes'
import Text from '../Text'
import Button from '../Button'
import { Link, useLocation } from 'react-router-dom'
import useElementOnScreen from '../../Utils/useIntersectionObserver'

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    display: 'flex',
    background: colors.white,
    width: '100%',
    marginTop: 30,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 65,
    paddingBlock: 70,
    marginInline: 'auto',
    maxWidth: 1100,
    [theme.breakpoints.up(1921)]: {
      maxWidth: 1300,
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down(1440)]: {
      maxWidth: 1000,
      justifyContent: 'space-between',
      gap: 0,
    },
    [theme.breakpoints.down(1100)]: {
      maxWidth: 900,
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down(960)]: {
      maxWidth: 700,
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down(760)]: {
      maxWidth: '100%',
      flexDirection: 'column',
      gap: 42,
      paddingInline: 16,
      paddingBlock: 40,
    },
  },
  leftContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: 1,
    [theme.breakpoints.down(1100)]: {
      width: '100%',
    },
  },
  aiDiv: {
    display: 'flex',
    gap: 8,
    width: 'fit-content',
    borderRadius: 24,
    paddingBlock: 8,
    paddingInline: 16,
    alignItems: 'center',
  },
  gradientText: {
    display: 'flex',
    lineHeight: 1.3,
    marginBottom: 16,
  },
  firstText: {
    display: 'flex',
    lineHeight: 1.3,
    marginTop: 16,
  },
  secondText: {
    display: 'flex',
    lineHeight: 1.3,
  },
  visualisations: {
    maxWidth: '75%',
    marginBottom: 48,
    [theme.breakpoints.down(760)]: {
      maxWidth: '100%',
      marginBottom: 32,
    },
  },
  getStartedButton: {
    maxWidth: 240,
    [theme.breakpoints.down(1440)]: {
      maxWidth: 160,
    },
  },
  heroButtons: {
    display: 'flex',
    gap: 58,
    alignItems: 'center',
    [theme.breakpoints.down(400)]: {
      flexDirection: 'column',
      gap: 32,
      alignItems: 'flex-start'
    }
  },
  h1: {
    fontSize: 42,
    color: '#344157',
    [theme.breakpoints.down(1280)]: {
      fontSize: 32
    },
    [theme.breakpoints.down(960)]: {
      fontSize: 24
    }
  },
  link: {
    color: colors.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
    [theme.breakpoints.down(400)]: {
      paddingLeft: 24
    }
  },
  rightContainer: {
    display: 'flex',
    flex: 1,
    [theme.breakpoints.down(1100)]: {
      maxWidth: 450,
    },
    [theme.breakpoints.down(960)]: {
      maxWidth: 350,
    },
    [theme.breakpoints.down(760)]: {
      maxWidth: '100%',
    },
  },
  svg: {
    [theme.breakpoints.down(960)]: {
      width: '100%'
    }
  }
}))

const HeroHome = () => {
  const classes = useStyles()

  const ref = useRef()
  const onScreen = useElementOnScreen(ref)

  const getStarted = (url) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'GetStartedButtonClick',
      buttonName: 'Get Started',
      buttonLocation: 'Hero Section',
      action: 'Click',
    })

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  return (
    <section
      className={classes.mainWrapper}
      ref={ref}
      style={{
        opacity: onScreen ? 1 : 0,
        translate: onScreen ? 'none' : '0 50px',
        transition: '1s ease-in-out',
      }}>
      <div className={classes.leftContainer}>
        <div className={classes.firstText}>
          <h1 className={classes.h1}>
            Automated Reporting for
          </h1>
        </div>
        <div className={classes.secondText}>
          <Text size='h2' weight='medium'>
            Growing Healthcare Clinics
          </Text>
        </div>
        <div className={classes.gradientText}></div>
        <div className={classes.visualisations}>
          <Text size='body'>
            Save time on reporting and unlock real-time performance insights from your Cliniko or Nookal data. Make smarter, data-driven decisions — without the spreadsheets.
          </Text>
        </div>
        <div className={classes.heroButtons}>
          <Button
            title='Get Started'
            buttonType='primaryGreen'
            className={classes.getStartedButton}
            onClick={() => getStarted('https://dashboard.dataplayer.io/signup')}
          />
          <Link to='/demo' onClick={() => {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
              event: 'RequestDemoButtonClick',
              buttonName: 'Request Demo',
              buttonLocation: 'Hero Section',
              action: 'Click',
            })
          }} className={classes.link}>
            <Text size='footnote' color={colors.primary}>
              Request a Demo
            </Text>
          </Link>
        </div>
      </div>
      <div className={classes.rightContainer}>
        <img
          src='https://dyl2njn37ytxk.cloudfront.net/website-sep9/home/heroImage.svg'
          className={classes.svg}
          alt='Dataplayer Power and Benefits - Hero Image'
          loading="lazy"
        />
      </div>
    </section>
  )
}

export default HeroHome
